import { FaroCoreApiTypes } from "@stellar/core-api-client";

/** A single description for feature roles on the company level. */
interface RequiredFeatureCompanyLevel {
  /** List of subscription roles to check in the available features of a company */
  companySubscriptionRoles: FaroCoreApiTypes.FeatureIdentifier[];
}

export interface HasFeatureCompanyLevelProps {
  /** The available features for the company */
  featuresAvailable: FaroCoreApiTypes.Feature[] | null;

  /** The required subscription roles for the company */
  requiredCompanyFeatures: FaroCoreApiTypes.FeatureIdentifier[];
}

/**
 * Object that determines all different features for users to get
 * access on the company level.
 */
export type RequiredFeaturesCompanyLevel<FeatureNameT extends string> = {
  [key in FeatureNameT]: RequiredFeatureCompanyLevel;
};

/**
 * Signature for the function hasUserValidFeatureCompanyLevel.
 */
export interface HasUserValidFeatureCompanyLevelProps<
  FeatureNameT extends string
> {
  /**
   * The internal name used in the UI to determine certain feature permissions,
   * e.g. "canViewCompanyGroups" is used to determine whether the user can see all company groups.
   */
  featureName: FeatureNameT;

  /**
   * The available features for the company.
   */
  featuresAvailable?: FaroCoreApiTypes.Feature[];

  /**
   * The default required features for the company level.
   * Only used for testing purposes.
   */
  defaultRequiredFeaturesCompanyLevel?: RequiredFeaturesCompanyLevel<FeatureNameT>;
}

/** Names for all features within the company level. */
export enum RequiredFeatureCompanyLevelName {
  /** Whether the user can see projects of the company related to the user. */
  canViewProjects = "canViewProjects",

  /** Whether the use has permission to access the groups page and view all company groups */
  canViewCompanyGroups = "canViewCompanyGroups",

  /** Whether the user has permission to see the company members list */
  canViewCompanyMembers = "canViewCompanyMembers",

  /** Whether the user has permission to see the company analytics page */
  canViewAnalytics = "canViewAnalytics",

  /** Whether the user has permission to see the company premium analytics pages */
  canViewPremiumAnalytics = "canViewPremiumAnalytics",

  /** Whether the user has permission to see the company integrations page */
  canViewIntegrations = "canViewIntegrations",

  /** Whether the user has permission to see the company settings page */
  canViewWorkspaceSettings = "canViewWorkspaceSettings",

  /** Whether the user has permission to create new projects */
  canCreateProjects = "canCreateProjects",
}
